import { getSafeExternalUrl } from "./safe-external-url";

export interface InlineSegment {
  text: string;
  /** Safe http(s) href; absent for plain text. */
  href?: string;
  /** Character offset of the segment in the source string; a stable key. */
  offset: number;
}

// `[text](url)`: text has no brackets, url has no whitespace or parentheses.
// URLs containing parentheses are therefore not supported.
const INLINE_LINK_PATTERN = /\[([^[\]]+)\]\(([^()\s]+)\)/g;

/**
 * Splits markdown-style `[text](url)` links out of a string. Links whose URL
 * is not a safe http(s) URL stay as their literal source text. Adjacent plain
 * text is merged into one segment.
 */
export function parseInlineLinks(value: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  // displayMetadata is untyped ops-entered JSON; don't crash the page on it.
  if (typeof value !== "string") {
    return segments;
  }

  const pushText = (text: string, offset: number) => {
    if (!text) {
      return;
    }
    const previous = segments.at(-1);
    if (previous && !previous.href) {
      previous.text += text;
      return;
    }
    segments.push({ text, offset });
  };

  let cursor = 0;
  for (const match of value.matchAll(INLINE_LINK_PATTERN)) {
    const [source, text, url] = match;
    const start = match.index;
    pushText(value.slice(cursor, start), cursor);
    const href = getSafeExternalUrl(url);
    if (href) {
      segments.push({ text, href, offset: start });
    } else {
      pushText(source, start);
    }
    cursor = start + source.length;
  }
  pushText(value.slice(cursor), cursor);

  return segments;
}
